import styled, {css, DefaultTheme} from 'styled-components'
import {ButtonStyleProps} from '@components/ui/button/Button.tsx'

export type StyledButtonProps = {
    $fullWidth?: boolean
    $shape: NonNullable<ButtonStyleProps['shape']>
    $size: NonNullable<ButtonStyleProps['size']>
    $variant: NonNullable<ButtonStyleProps['variant']>
}

export const makeButtonBaseStyle = (
    theme: DefaultTheme,
    {$fullWidth, $shape, disabled}: Omit<StyledButtonProps, '$variant'> & {disabled: boolean}
) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    word-break: break-word;
    font-weight: 500;
    transition: ${theme.transition};
    ${$shape == 'rectangle' &&
    $fullWidth &&
    css`
        width: 100%;
    `}
    ${$shape == 'rectangle' &&
    css`
        height: max-content;
    `}
    ${$shape == 'square' &&
    css`
        flex-shrink: 0;
    `}
  ${disabled
        ? css`
              cursor: not-allowed;
          `
        : css`
              cursor: pointer;
              &:focus-visible {
                  box-shadow: ${`${theme.shadows.xs}, 0px 0px 0px 4px ${theme.palette.neutral['100']}`};
              }
          `}
`

export const makeButtonSizeStyle = (theme: DefaultTheme) =>
    ({
        xs: {
            rectangle: css`
                gap: ${theme.spacing * 2}px;
                padding: ${theme.spacing * 0.25}px ${theme.spacing * 2.5}px;
                ${theme.typography.textXs};
                border-radius: 8px;
                & svg {
                    width: 15px;
                    height: 15px;
                }
            `,
            roundedRectangle: css`
                gap: ${theme.spacing * 2}px;
                padding: ${theme.spacing * 0.25}px ${theme.spacing * 1.5}px;
                ${theme.typography.textXs};
                border-radius: 18px;
                & svg {
                    width: 15px;
                    height: 15px;
                }
            `,
            square: css`
                width: 24px;
                height: 24px;
                border-radius: 8px;
                ${theme.typography.textXs};
                & svg {
                    width: 15px;
                    height: 15px;
                }
            `,
            circle: css`
                width: 24px;
                height: 24px;
                border-radius: 50%;
                ${theme.typography.textXs};
                & svg {
                    width: 15px;
                    height: 15px;
                }
            `,
            text: css`
                gap: ${theme.spacing * 2}px;
                ${theme.typography.textXs};
                & svg {
                    width: 15px;
                    height: 15px;
                }
            `
        },
        sm: {
            rectangle: css`
                gap: ${theme.spacing * 2}px;
                padding: ${theme.spacing}px ${theme.spacing * 2}px;
                border-radius: 8px;
                ${theme.typography.textSm};
                /* The .custom class is used to enable icon resizing from the component */
                & svg:not(.custom) {
                    width: 15px;
                    height: 15px;
                }
            `,
            roundedRectangle: css`
                gap: ${theme.spacing * 2}px;
                padding: ${theme.spacing}px ${theme.spacing * 1.5}px;
                border-radius: 18px;
                ${theme.typography.textSm};
                & svg {
                    width: 15px;
                    height: 15px;
                }
            `,
            square: css`
                width: 28px;
                height: 28px;
                border-radius: 8px;
                ${theme.typography.textSm};
                & svg {
                    width: 15px;
                    height: 15px;
                }
            `,
            circle: css`
                width: 28px;
                height: 28px;
                border-radius: 50%;
                ${theme.typography.textSm};
                & svg {
                    width: 15px;
                    height: 15px;
                }
            `,
            text: css`
                gap: ${theme.spacing * 2}px;
                ${theme.typography.textSm};
                & svg {
                    width: 15px;
                    height: 15px;
                }
            `
        },
        md: {
            rectangle: css`
                gap: ${theme.spacing * 2}px;
                padding: ${theme.spacing * 1.5}px ${theme.spacing * 2.5}px;
                border-radius: 8px;
                ${theme.typography.textSm};
                /* The .custom class is used to enable icon resizing from the component */
                & svg:not(.custom) {
                    width: 15px;
                    height: 15px;
                }
            `,
            roundedRectangle: css`
                gap: ${theme.spacing * 2}px;
                padding: ${theme.spacing * 1.5}px ${theme.spacing * 2}px;
                border-radius: 18px;
                ${theme.typography.textSm};
                & svg {
                    width: 15px;
                    height: 15px;
                }
            `,
            square: css`
                width: 32px;
                height: 32px;
                border-radius: 8px;
                ${theme.typography.textSm};
                & svg {
                    width: 15px;
                    height: 15px;
                }
            `,
            circle: css`
                width: 32px;
                height: 32px;
                border-radius: 50%;
                ${theme.typography.textSm};
                & svg {
                    width: 15px;
                    height: 15px;
                }
            `,
            text: css`
                gap: ${theme.spacing * 2}px;
                ${theme.typography.textSm};
                & svg {
                    width: 15px;
                    height: 15px;
                }
            `
        },
        lg: {
            rectangle: css`
                gap: ${theme.spacing * 2}px;
                padding: ${theme.spacing * 1.5}px ${theme.spacing * 4.5}px;
                border-radius: 8px;
                ${theme.typography.textMd};
                & svg {
                    width: 20px;
                    height: 20px;
                }
            `,
            roundedRectangle: css`
                gap: ${theme.spacing * 2}px;
                padding: ${theme.spacing * 1.5}px ${theme.spacing * 3.5}px;
                border-radius: 18px;
                ${theme.typography.textMd};
                & svg {
                    width: 20px;
                    height: 20px;
                }
            `,
            square: css`
                width: 40px;
                height: 40px;
                border-radius: 8px;
                ${theme.typography.textMd};
                & svg {
                    width: 20px;
                    height: 20px;
                }
            `,
            circle: css`
                width: 40px;
                height: 40px;
                border-radius: 50%;
                ${theme.typography.textMd};
                & svg {
                    width: 20px;
                    height: 20px;
                }
            `,
            text: css`
                gap: ${theme.spacing * 2}px;
                ${theme.typography.textMd};
                & svg {
                    width: 20px;
                    height: 20px;
                }
            `
        },
        xl: {
            rectangle: css`
                gap: ${theme.spacing * 2}px;
                padding: ${theme.spacing * 3}px ${theme.spacing * 7}px;
                border-radius: 8px;
                ${theme.typography.textLg};
                & svg {
                    width: 20px;
                    height: 20px;
                }
            `,
            roundedRectangle: css`
                gap: ${theme.spacing * 2}px;
                padding: ${theme.spacing * 3}px ${theme.spacing * 5}px;
                border-radius: 18px;
                ${theme.typography.textLg};
                & svg {
                    width: 20px;
                    height: 20px;
                }
            `,
            square: css`
                width: 48px;
                height: 48px;
                ${theme.typography.textLg};
                & svg {
                    width: 20px;
                    height: 20px;
                }
            `,
            circle: css`
                width: 48px;
                height: 48px;
                border-radius: 50%;
                ${theme.typography.textLg};
                & svg {
                    width: 20px;
                    height: 20px;
                }
            `,
            text: css`
                gap: ${theme.spacing * 2}px;
                ${theme.typography.textLg};
                & svg {
                    width: 20px;
                    height: 20px;
                }
            `
        }
    }) as const satisfies Record<
        NonNullable<StyledButtonProps['$size']>,
        Record<NonNullable<StyledButtonProps['$shape']>, ReturnType<typeof css>>
    >

export const makeButtonVariantStyle = (theme: DefaultTheme, disabled: boolean) =>
    ({
        primary: css`
            color: ${theme.palette.neutral.white};
            box-shadow: ${theme.shadows.xs};
            ${disabled
                ? css`
                      background: ${theme.palette.primary['400']};
                  `
                : css`
                      background: ${theme.palette.primary['600']};
                      &:hover {
                          color: ${theme.palette.neutral.white};
                          background: ${theme.palette.primary['700']};
                      }
                      &:active,
                      &.active {
                          background: ${theme.palette.primary['800']};
                      }
                  `}
        `,
        primaryDanger: css`
            color: ${theme.palette.neutral.white};
            box-shadow: ${theme.shadows.xs};
            ${disabled
                ? css`
                      background: ${theme.palette.danger['400']};
                  `
                : css`
                      background: ${theme.palette.danger['600']};
                      &:hover {
                          color: ${theme.palette.neutral.white};
                          background: ${theme.palette.danger['700']};
                      }
                      &:active,
                      &.active {
                          background: ${theme.palette.danger['800']};
                      }
                  `}
        `,
        secondary: css`
            background: ${theme.palette.neutral.white};
            outline: 1px solid ${theme.palette.neutral['300']};
            box-shadow: ${theme.shadows.sm};
            ${disabled
                ? css`
                      color: ${theme.palette.neutral['400']};
                  `
                : css`
                      color: ${theme.palette.neutral['700']};
                      &:hover {
                          color: ${theme.palette.neutral['700']};
                          background: ${theme.palette.neutral['50']};
                      }
                      &:active,
                      &.active {
                          background: ${theme.palette.neutral['100']};
                      }
                  `}
        `,
        tertiary: css`
            color: ${theme.palette.neutral['700']};
            text-decoration: underline;
            ${disabled
                ? css`
                      color: ${theme.palette.neutral['400']};
                  `
                : css`
                      &:hover {
                          color: ${theme.palette.neutral['700']};
                      }
                  `}
        `,
        tertiaryPrimary: css`
            color: ${theme.palette.primary['500']};
            text-decoration: underline;
            ${disabled
                ? css`
                      color: ${theme.palette.primary['400']};
                  `
                : css`
                      &:hover {
                          color: ${theme.palette.primary['600']};
                      }
                  `}
        `,
        tertiaryDanger: css`
            color: ${theme.palette.danger['600']};
            text-decoration: underline;
            ${disabled
                ? css`
                      color: ${theme.palette.danger['400']};
                  `
                : css`
                      &:hover {
                          color: ${theme.palette.danger['700']};
                      }
                  `}
        `,
        ghost: css`
            ${disabled
                ? css`
                      color: ${theme.palette.neutral['400']};
                  `
                : css`
                      color: ${theme.palette.neutral['500']};
                      &:hover {
                          color: ${theme.palette.neutral['700']};
                      }
                  `}
        `,
        secondaryDanger: css`
            background: ${theme.palette.danger['300']}${theme.opacities[60]};
            ${disabled
                ? css`
                      color: ${theme.palette.neutral[400]};
                      background: ${theme.palette.danger['300']}${theme.opacities[30]};
                  `
                : css`
                      color: ${theme.palette.neutral[500]};
                      &:hover {
                          background: ${theme.palette.danger['400']}${theme.opacities[70]};
                      }
                      &:active,
                      &.active {
                          background: ${theme.palette.danger['500']}${theme.opacities[80]};
                      }
                  `}
        `,
        ghostDanger: css`
            ${disabled
                ? css`
                      color: ${theme.palette.danger['400']};
                  `
                : css`
                      color: ${theme.palette.danger['500']};
                      & > svg {
                          fill: ${theme.palette.danger['500']};
                      }
                      &:hover {
                          color: ${theme.palette.danger['700']};
                          background-color: transparent;
                          & > svg {
                              fill: ${theme.palette.danger['700']};
                          }
                      }
                  `}
        `,
        primaryNeutral: css`
            color: ${theme.palette.neutral.white};
            box-shadow: ${theme.shadows.xs};
            ${disabled
                ? css`
                      background: ${theme.palette.neutral['100']};
                      color: ${theme.palette.neutral['400']};
                  `
                : css`
                      background: ${theme.palette.neutral['950']};
                      &:hover {
                          background: ${theme.palette.neutral['800']};
                      }
                      &:active,
                      &.active {
                          background: ${theme.palette.neutral['700']};
                      }
                  `}
        `
    }) as const satisfies Record<NonNullable<StyledButtonProps['$variant']>, ReturnType<typeof css>>

export const StyledButton = styled.button<StyledButtonProps & {disabled?: boolean}>(
    ({theme, ...props}) => css`
        ${makeButtonSizeStyle(theme)[props.$size][props.$shape]};
        ${makeButtonVariantStyle(theme, !!props.disabled)[props.$variant]};
        ${makeButtonBaseStyle(theme, {
            $fullWidth: props.$fullWidth,
            $shape: props.$shape,
            $size: props.$size,
            disabled: !!props.disabled
        })};
    `
)
